import type { JSONContent } from "@tiptap/core"
import Mention from "@tiptap/extension-mention"
import StarterKit from "@tiptap/starter-kit"
import type { SuggestionOptions } from "@tiptap/suggestion"
import { MarkdownManager } from "@tiptap/markdown"
import { Markdown } from "@tiptap/markdown"
import { Lexer, type Token } from "marked"

export type MentionUser = {
  id: string
  label: string
}

export type ReplyMentionSuggestion = Omit<SuggestionOptions<MentionUser>, "editor">

const mentionPattern = /^\[((?:\\.|[^\\\]])*)\]\(mention:([1-9]\d*)\)/
const mentionSearchPattern = /\[((?:\\.|[^\\\]])*)\]\(mention:[1-9]\d*\)/
const markdownPunctuation = /[\\`*_{}\[\]()#+\-.!|>~]/g
const mentionHtmlEntities = /&(amp|lt|gt);/g

export function escapeMentionLabel(label: string): string {
  return label
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(markdownPunctuation, "\\$&")
}

export function unescapeMentionLabel(label: string): string {
  return label
    .replace(/\\([\\`*_{}\[\]()#+\-.!|>~])/g, "$1")
    .replace(mentionHtmlEntities, (_, entity: string) => ({
      amp: "&",
      lt: "<",
      gt: ">",
    })[entity] ?? _)
}

const ReplyMention = Mention.extend({
  parseMarkdown: (token, helpers) =>
    helpers.createNode("mention", {
      id: String(token.attributes?.id ?? ""),
      label: String(token.attributes?.label ?? ""),
    }),
  renderMarkdown: (node: JSONContent) => {
    const id = String(node.attrs?.id ?? "")
    const label = String(node.attrs?.label ?? id)

    const escapedLabel = escapeMentionLabel(label)
    if (!/^[1-9]\d*$/.test(id)) {
      return `@${escapedLabel}`
    }

    return `[@${escapedLabel}](mention:${id})`
  },
  markdownTokenizer: {
    name: "mention",
    level: "inline",
    start(source: string) {
      return source.search(mentionSearchPattern)
    },
    tokenize(source: string) {
      const match = source.match(mentionPattern)
      if (!match) return undefined

      const [, escapedLabel, id] = match
      if (!escapedLabel.startsWith("@")) return undefined

      return {
        type: "mention",
        raw: match[0],
        attributes: { id, label: unescapeMentionLabel(escapedLabel.slice(1)) },
      }
    },
  },
})

export function createReplyMentionExtension(
  suggestion: ReplyMentionSuggestion = {},
) {
  return ReplyMention.configure({
    HTMLAttributes: { class: "reply-mention" },
    renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
    suggestion,
  })
}

export function createReplyEditorExtensions(suggestion: ReplyMentionSuggestion = {}) {
  return [
    StarterKit,
    createReplyMentionExtension(suggestion),
    Markdown,
  ]
}

export function createReplyMarkdownManager(): MarkdownManager {
  return new MarkdownManager({
    extensions: [StarterKit, createReplyMentionExtension()],
  })
}

export function markdownToReplyDocument(markdown: string): JSONContent {
  return createReplyMarkdownManager().parse(markdown)
}

export function replyDocumentToMarkdown(document: JSONContent): string {
  return createReplyMarkdownManager().serialize(document)
}

export function hasUnsupportedReplyMarkdown(markdown: string): boolean {
  return containsUnsupportedReplyToken(Lexer.lex(markdown))
}

function containsUnsupportedReplyToken(tokens: Token[]): boolean {
  return tokens.some((token) => {
    if (token.type === "code" || token.type === "codespan") return false
    if (token.type === "html" || token.type === "image" || token.type === "table" || token.type === "def") {
      return true
    }
    if (token.type === "list_item" && token.task) return true
    if (token.type === "text" && /\{\{attachment:\d+\}\}/.test(token.text)) return true

    if (token.type === "list" && token.items.some((item: Token) => containsUnsupportedReplyToken([item]))) {
      return true
    }

    if ("tokens" in token && Array.isArray(token.tokens)) {
      return containsUnsupportedReplyToken(token.tokens)
    }

    return false
  })
}
